// src/Components/Frontend/MobileOtpLogin.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

export default function MobileOtpLogin() {
    const navigate = useNavigate();

    const [mobile, setMobile] = useState("");
    const [otp, setOtp] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [mobileError, setMobileError] = useState("");
    const [otpError, setOtpError] = useState("");
    const [apiError, setApiError] = useState("");

    // =========================
    // VALIDATE MOBILE
    // =========================
    const validateMobile = () => {
        if (!mobile.trim()) {
            setMobileError("Mobile number is required");
            return false;
        }
        if (!/^\d{10}$/.test(mobile)) {
            setMobileError("Enter a valid 10-digit mobile number");
            return false;
        }
        setMobileError("");
        return true;
    };

    // =========================
    // SEND OTP
    // =========================
    const handleGetOtp = async () => {
        if (!validateMobile()) return;

        setOtpLoading(true);
        setApiError("");

        try {
            const res = await fetch("https://orisoftpl.com/backend/api/frontend/login/send-otp", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({ mobile_no: mobile }),
            });

            const data = await res.json();

            if (!res.ok) {
                setApiError(data.message || "Failed to send OTP");
                return;
            }

            setOtpSent(true);

        } catch (error) {
            setApiError("Something went wrong");
        } finally {
            setOtpLoading(false);
        }
    };

    // =========================
    // VERIFY OTP & LOGIN
    // =========================
    const handleLogin = async (e) => {
        e.preventDefault();

        if (!validateMobile()) return;

        if (!otp.trim()) {
            setOtpError("OTP is required");
            return;
        }
        setOtpError("");

        setLoading(true);
        setApiError("");

        try {
            const res = await fetch("https://orisoftpl.com/backend/api/frontend/login/verify-otp", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({ mobile_no: mobile, otp }),
            });

            const data = await res.json();

            if (!res.ok) {
                setApiError(data.message || "Invalid OTP");
                return;
            }

            localStorage.setItem("user_token", data.token);
            localStorage.setItem("user_data", JSON.stringify(data.user));

            navigate("/");

        } catch (error) {
            setApiError("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4">
            <div className="w-full max-w-2xl">

                <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-black">User Login</h2>
                </div>

                <div className="card bg-white shadow-xl p-8">

                    {apiError && (
                        <div className="alert border-red-500 mb-4 text-red-500 text-sm">
                            <span>{apiError}</span>
                        </div>
                    )}

                    {otpSent && (
                        <div className="alert border-green-500 mb-4 text-green-600 text-sm">
                            <span>OTP sent to {mobile}</span>
                        </div>
                    )}

                    <form onSubmit={handleLogin}>
                        <div className="grid grid-cols-2 gap-5">

                            {/* Mobile Number */}
                            <div>
                                <label className="block font-semibold text-sm mb-1">
                                    Mobile Number <span className="text-red-500">*</span>
                                </label>
                                <div className="flex">
                                    <input
                                        type="text"
                                        placeholder="Enter your mobile number"
                                        maxLength={10}
                                        value={mobile}
                                        onChange={(e) => {
                                            setMobile(e.target.value);
                                            setMobileError("");
                                            setApiError("");
                                        }}
                                        className={`input input-bordered input-md w-full rounded-r-none ${mobileError ? "input-error" : ""}`}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleGetOtp}
                                        disabled={otpLoading}
                                        className="btn btn-error btn-md rounded-l-none text-white"
                                    >
                                        {otpLoading ? (
                                            <span className="loading loading-spinner loading-xs"></span>
                                        ) : otpSent ? "Resend OTP" : "Get OTP"}
                                    </button>
                                </div>
                                {mobileError && (
                                    <p className="text-red-500 text-xs mt-1">{mobileError}</p>
                                )}
                            </div>

                            {/* OTP */}
                            <div>
                                <label className="block font-semibold text-sm mb-1">
                                    OTP <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Enter your OTP"
                                    maxLength={6}
                                    value={otp}
                                    onChange={(e) => {
                                        setOtp(e.target.value);
                                        setOtpError("");
                                        setApiError("");
                                    }}
                                    disabled={!otpSent}
                                    className={`input input-bordered input-md w-full ${otpError ? "input-error" : ""} ${!otpSent ? "input-disabled" : ""}`}
                                />
                                {otpError && (
                                    <p className="text-red-500 text-xs mt-1">{otpError}</p>
                                )}
                            </div>

                        </div>

                        {/* Submit */}
                        <div className="mt-6">
                            <button
                                type="submit"
                                disabled={loading || !otpSent}
                                className={`btn btn-primary w-full text-white ${loading || !otpSent ? "btn-disabled" : ""}`}
                            >
                                {loading ? (
                                    <>
                                        <span className="loading loading-spinner loading-xs"></span>
                                        Verifying...
                                    </>
                                ) : "Login"}
                            </button>
                        </div>

                    </form>
                </div>
            </div>
        </div>
    );
}